/* global subscribe, PUB_SUB_EVENTS */
/*
 * The trade-in in the cart drawer and on the cart page. The phone page's step saves it as cart
 * attributes (assets/pf-scroll-pilot-trade-in.js, contract v2); this shows it under the phone it goes
 * with: "Trade-in: iPhone 13 · $306 cash after we check it", with Edit (back to the phone's page)
 * and Remove. When that phone leaves the cart, the trade-in comes off too. A trade-in saved before
 * its phone is added is left alone: the phone page saves it first.
 *
 * Loaded on every page while "Trade-in step on phone product pages" is on (layout/theme.liquid), for
 * U.S. shoppers and for anyone else whose cart still has a trade-in. It writes the cart only when the
 * shopper removes the trade-in or its phone, or when a trade-in is on a cart outside the U.S.
 */
(() => {
  if (typeof document === "undefined") return;
  if (window.plugTradeInCartInitialized) return;
  window.plugTradeInCartInitialized = true;

  // The stylesheet comes with the script tag (data-css), and loads only once a row is shown.
  const CSS_URL = document.currentScript?.dataset?.css || "";
  // U.S. only: plug-core turns down trade-ins on orders shipping elsewhere. The tag says whether the
  // page was drawn for a U.S. shopper; a cart priced in another currency means the same.
  const US_PAGE = document.currentScript?.dataset?.us !== "false";
  const outsideUs = (current) =>
    !US_PAGE || Boolean(current?.currency && current.currency !== "USD");
  const STORE_KEY = "plug_trade_in_pdp_v1";
  // Keep in step with CART_KEYS in pf-scroll-pilot-trade-in.js (tests check they match).
  const CART_KEYS = [
    "Trade-in",
    "_trade_in",
    "_trade_in_v",
    "_trade_in_for_product",
    "_trade_in_details",
    "_trade_in_product_id",
    "_trade_in_condition_id",
    "_trade_in_name",
    "_trade_in_condition",
    "_trade_in_quote_cents",
    "_trade_in_payout",
    "_trade_in_quoted_at",
  ];
  const BONUS_RATE = 0.1;
  // The rows the phone's line can be in: the cart drawer and the cart page.
  const ROWS = ["CartDrawer-Item-", "CartItem-"];

  let cart = null;
  // Whether the trade-in's phone was in the cart at the last read (null: no trade-in).
  let hadPhone = null;
  let observer = null;
  let error = "";

  const cartUrl = (path) => {
    const root = window.Shopify?.routes?.root || "/";
    return `${root.endsWith("/") ? root : `${root}/`}${path}`;
  };

  const escapeHtml = (value) =>
    String(value ?? "").replace(
      /[&<>"']/g,
      (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch])
    );

  // Whole dollars, rounded down, like the phone page ("$306").
  const dollars = (cents) => `$${Math.floor(Math.max(0, cents) / 100).toLocaleString("en-US")}`;

  function tradeIn(current) {
    const a = current?.attributes || {};
    if (a._trade_in !== "1") return null;
    const items = Array.isArray(current.items) ? current.items : [];
    const index = items.findIndex((item) => String(item.product_id) === a._trade_in_for_product);
    return { a, index, phone: items[index] || null };
  }

  // Reusely's product name, short (same as shortName in pf-scroll-pilot-trade-in.js; tests check):
  // "Apple iPhone 13 Unlocked 128GB" -> "iPhone 13 · 128GB · Unlocked".
  const CARRIERS =
    /\s+(unlocked|verizon|at&t|att|t-mobile|tmobile|sprint|us cellular|cricket|metro(?:pcs| by t-mobile)?|boost(?: mobile)?|xfinity|spectrum|visible|other)\b(?=\s|$)/i;
  function shortName(name) {
    const clean = String(name ?? "")
      .replace(/[\u0000-\u001f<>]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 80);
    let rest = clean.replace(/^(apple|samsung|google)\s+/i, "");
    const storage = rest.match(/\s+(\d+\s?(?:gb|tb))\b/i);
    if (storage) rest = rest.replace(storage[0], "");
    const carrier = rest.match(CARRIERS);
    if (carrier) rest = rest.replace(carrier[0], "");
    const details = [storage?.[1].replace(/\s/g, "").toUpperCase(), carrier?.[1]].filter(Boolean);
    return { model: rest.trim() || clean || "Phone", details };
  }

  // { phone: "iPhone 13 · 128GB · Unlocked", value: "$306 cash after we check it" }
  function summary(a) {
    const { model, details } = shortName(a._trade_in_name || "Your phone");
    const phone = [model, ...details].join(" · ");
    if (a._trade_in_details !== "quoted") return { phone, value: "Priced when it arrives" };
    const cents = Number(a._trade_in_quote_cents) || 0;
    const amount =
      a._trade_in_payout === "cash"
        ? `${dollars(cents)} cash`
        : a._trade_in_payout === "card"
        ? `${dollars(cents)} back`
        : `${dollars(Math.round(cents * (1 + BONUS_RATE)))} Plug gift card`;
    return { phone, value: `${amount} after we check it` };
  }

  function rowHtml(current) {
    const { a, phone } = tradeIn(current);
    const { phone: name, value } = summary(a);
    return `<div class="pf-ti-cart" data-ti-cart-row>
        <p class="pf-ti-cart__line"><strong>Trade-in:</strong> ${escapeHtml(name)}</p>
        <p class="pf-ti-cart__value">${escapeHtml(value)}</p>
        <p class="pf-ti-cart__actions">
          <a href="${escapeHtml(phone?.url || cartUrl("cart"))}">Edit</a>
          <button type="button" data-ti-cart-remove>Remove</button>
        </p>${error ? `<p class="pf-ti-cart__error" role="alert">${escapeHtml(error)}</p>` : ""}
      </div>`;
  }

  function loadStyles() {
    if (!CSS_URL || document.querySelector("link[data-ti-cart-css]")) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.dataset.tiCartCss = "";
    link.href = CSS_URL;
    document.head.appendChild(link);
  }

  // Puts the row under the phone's line wherever the cart is shown, and keeps it there while the
  // drawer and cart page redraw themselves.
  function render() {
    const found = tradeIn(cart);
    const wanted = found && found.index >= 0 && !outsideUs(cart);
    document.querySelectorAll("[data-ti-cart-row]").forEach((row) => {
      const line = row.closest(ROWS.map((prefix) => `[id^="${prefix}"]`).join(","));
      if (!wanted || line?.id.replace(/^\D+-/, "") !== String(found.index + 1)) row.remove();
    });
    if (!wanted) {
      observer?.disconnect();
      observer = null;
      return;
    }
    loadStyles();
    ROWS.forEach((prefix) => {
      const details = document.querySelector(`#${prefix}${found.index + 1} .cart-item__details`);
      if (!details) return;
      const existing = details.querySelector("[data-ti-cart-row]");
      const html = rowHtml(cart);
      if (existing?.outerHTML === html) return;
      if (existing) existing.outerHTML = html;
      else details.insertAdjacentHTML("beforeend", html);
    });
    if (!observer && "MutationObserver" in window) {
      let queued = false;
      observer = new MutationObserver(() => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(() => {
          queued = false;
          // The cart's lines changed on the page (a line removed or added) without a cart event
          // reaching us: read the cart again.
          if (lineCount() !== shownLines) return refresh();
          const now = tradeIn(cart);
          const missing =
            now?.index >= 0 &&
            ROWS.some(
              (prefix) =>
                document.querySelector(`#${prefix}${now.index + 1} .cart-item__details`) &&
                !document.querySelector(`#${prefix}${now.index + 1} [data-ti-cart-row]`)
            );
          if (missing) render();
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  // Lines drawn on the page (drawer and cart page), to notice a removal the theme didn't announce.
  const lineCount = () =>
    document.querySelectorAll(ROWS.map((prefix) => `[id^="${prefix}"]`).join(",")).length;
  let shownLines = 0;

  async function read() {
    try {
      const response = await fetch(cartUrl(`cart.js?tic=${Date.now()}`), {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      return response.ok ? await response.json() : null;
    } catch {
      return null;
    }
  }

  // Takes the trade-in off the cart and out of the phone page's memory, then tells the phone page.
  async function clear(reason) {
    let ok = false;
    try {
      const response = await fetch(cartUrl("cart/update.js"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ attributes: Object.fromEntries(CART_KEYS.map((key) => [key, ""])) }),
      });
      ok = response.ok;
      if (ok) cart = await response.json();
    } catch {}
    if (!ok) {
      error = "We couldn't remove your trade-in. Try again.";
      render();
      return false;
    }
    error = "";
    hadPhone = null;
    try {
      window.sessionStorage.removeItem(STORE_KEY);
    } catch {}
    document.dispatchEvent(new CustomEvent("pf:trade-in-cart-changed", { detail: { reason } }));
    render();
    return true;
  }

  async function refresh() {
    shownLines = lineCount();
    const next = await read();
    if (!next) return;
    cart = next;
    const found = tradeIn(cart);
    // A trade-in carried over from a U.S. visit (the shopper changed country): it comes off.
    if (found && outsideUs(cart)) {
      await clear("outside-us");
      return;
    }
    const hasPhone = found ? found.index >= 0 : null;
    // The phone it goes with just left the cart: the trade-in goes too.
    if (found && hadPhone === true && hasPhone === false) {
      await clear("phone-removed");
      return;
    }
    hadPhone = hasPhone;
    render();
  }

  document.addEventListener("click", (event) => {
    if (!event.target.closest?.("[data-ti-cart-remove]")) return;
    event.preventDefault();
    clear("removed");
  });

  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => refresh());
  }
  // The phone page's step changed the trade-in.
  document.addEventListener("pf:trade-in-changed", () => refresh());

  window.PlugTradeInCart = { refresh, summary, shortName, CART_KEYS };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => refresh(), { once: true });
  } else {
    refresh();
  }
})();
